<template>
    <v-layout style="position: relative; min-height: 200px;" align-center justify-center>
        <no-data v-if="!cropProfile.BerriesForecastProfileID" message="No crop profile found"></no-data>
        <loading v-else-if="loading"></loading>
        <no-data v-else-if="!cropForecasts" message="Unable to load crop profile data"></no-data>        
        <div v-else>
            <no-data v-if="!cropProfile.CropArea" message="Crop variety has no area defined"></no-data>
            <crop-profile-chart :data="chartData" :options="chartOptions" ref="cropProfileChart"></crop-profile-chart>
        </div>
    </v-layout>
</template>

<script>
import Loading from '@/components/generic/Loading.vue';
import NoData from '@/components/generic/NoData.vue';
import CropProfileChart from '@/components/crops/CropProfileChart.vue';
import UtilService from '@/services/utilService.js';
import forecastingUtilService from '@/services/forecastingUtilService';
import momentCache from "@/services/momentCache";
import Chart from 'chart.js'

export default {
    components: {
        Loading,
        NoData,
        CropProfileChart
    },
    props: [
        'financialYearCalendars',
        'cropProfile',
        'cropForecasts',
        'displayValueTypeCode',        
        'loading'
    ],
    data(){
        return {       
        };
    },
    computed: {
        startDate(){
            let date = forecastingUtilService.getFinancialStartDateFromProfile(this.cropProfile, this.financialYearCalendars);

            this.forecastValues.forEach(forecast => {
                if (date == null || momentCache.getMoment(forecast.date).isBefore(momentCache.getMoment(date))) {
                    date = momentCache.getMoment(forecast.date).format("YYYY-MM-DD");
                }
            });

            return date;
        },
        endDate(){
            let date = forecastingUtilService.getFinancialEndDateFromProfile(this.cropProfile, this.financialYearCalendars);

            this.forecastValues.forEach(forecast => {
                if (date == null || momentCache.getMoment(forecast.date).isAfter(momentCache.createMoment(date).add(1, 'week'))) {
                    date = momentCache.getMoment(forecast.date).format("YYYY-MM-DD");
                }
            });

            return momentCache.createMoment(date).add(1, 'week').format("YYYY-MM-DD");
        },
        dates(){
            let dates = [this.startDate];
            let nextDate = momentCache.getMoment(UtilService.weeksForward(this.startDate, 1)).format("YYYY-MM-DD");

            while(nextDate < this.endDate){

                /*if (forecastingUtilService.getFinancialWeek(nextDate, this.financialYearCalendars) == 53) {
                    nextDate = momentCache.getMoment(UtilService.weeksForward(nextDate, 1)).format("YYYY-MM-DD");
                }*/        
                
                dates.push(nextDate);   
                
                let lastDate = dates[dates.length - 1];
                nextDate = momentCache.getMoment(UtilService.weeksForward(lastDate, 1)).format("YYYY-MM-DD");
            }

            return dates;
        },
        budgetValues(){
            if (this.cropProfile.BudgetValues) {
                return this.cropProfile.BudgetValues
                    .map(x => {
                        return {
                            date: this.getDateForBudgetValue(x),
                            value: this.formatPercentage(x.BudgetValue || 0)
                        }
                    });
            }

            return [];
        },
        forecastValues(){
            return this.cropForecasts
                .filter(x => (x.cropLocationId || x.CropLocationID) == this.cropProfile.CropLocationID && (x.varietyId || x.VarietyID) == this.cropProfile.Variety.VarietyID)
                .map(x => {
                    return {
                        date: x.forecastDate || x.ForecastDate,
                        value: this.formatKg(x.forecastValue || x.ForecastValue || 0)
                    }
                });
        },
        adjustedValues(){
            return this.cropForecasts
                .filter(x => (x.cropLocationId || x.CropLocationID) == this.cropProfile.CropLocationID && (x.varietyId || x.VarietyID) == this.cropProfile.Variety.VarietyID)
                .map(x => {
                    return {
                        date: x.forecastDate || x.ForecastDate,
                        value: this.formatKg(x.adjustedValue || x.AdjustedValue || 0)
                    }
                });
        },
        actualValues(){
            return this.cropForecasts
                .filter(x => (x.cropLocationId || x.CropLocationID) == this.cropProfile.CropLocationID && (x.varietyId || x.VarietyID) == this.cropProfile.Variety.VarietyID)
                .map(x => {

                    return {
                        date: x.forecastDate || x.ForecastDate,
                        value: this.formatKg(x.actualValue || x.ActualValue || 0)
                    }
                });
        },
        timeline(){
            let timeline = [];

            for(let i = 0; i < this.dates.length; i++){
                let startDate = this.dates[i];
                let endDate = null;
                if(i < this.dates.length - 1){
                    endDate = UtilService.daysBack(this.dates[i + 1], 1); // The end of A is 1 day before the start of B
                } else {
                    // If this is the last date of the timeline, then we want to finish with our profile end date
                    endDate = this.endDate
                }

                startDate = startDate + "T00:00:00"
                endDate = endDate + "T00:00:00"           

                timeline.push({
                    startDate: startDate,
                    endDate: endDate,
                    budgetValues: this.budgetValues
                        .filter(x => UtilService.dateContainedWithin(startDate, endDate, x.date)),
                    forecastValues: this.forecastValues
                        .filter(x => UtilService.dateContainedWithin(startDate, endDate, x.date)),
                    adjustedValues: this.adjustedValues
                        .filter(x => UtilService.dateContainedWithin(startDate, endDate, x.date)),
                    actualValues: this.actualValues
                        .filter(x => UtilService.dateContainedWithin(startDate, endDate, x.date))
                });
            }

            return timeline;
        },
        chartBudgetData(){
            return this.timeline.map(x => {
                let value = null
                if(x.budgetValues.length > 0){
                    value = x.budgetValues.map(y => y.value).reduce((a, b) => a + b)                    
                }

                return {
                    x: new Date(x.startDate),
                    y: value
                };
            });
        },
        chartActualData(){
            return this.timeline.map(x => {
                let value = null
                if(x.actualValues.length > 0){
                    value = x.actualValues.map(y => y.value).reduce((a, b) => a + b)
                }

                return {
                    x: new Date(x.startDate),
                    y: value
                };
            });
        },
        chartForecastData(){
            return this.timeline.map(x => {
                let value = null
                if(x.forecastValues.length > 0){
                    value = x.forecastValues.map(y => y.value).reduce((a, b) => a + b)
                }

                return {
                    x: new Date(x.startDate),
                    y: value
                };
            });
        },
        chartAdjustedData(){
            return this.timeline.map(x => {
                let value = null
                if(x.adjustedValues.length > 0){
                    value = x.adjustedValues.map(y => y.value).reduce((a, b) => a + b)
                }

                return {
                    x: new Date(x.startDate),
                    y: value
                };
            });
        },
        chartLabels(){
            return this.timeline.map((x, i) => {
                return {
                    x: new Date(x.startDate),
                    value: `Week ${(i + 1)}`
                };
            }).filter(x => x != null);
        },
        chartData(){
            let data = {
                labels: this.chartLabels,
                datasets: []
            }

            if (this.timeline.some(t => t.budgetValues.length > 0)) {
                data.datasets.push(
                    {
                        label: "Profile",
                        borderColor: "#F90",
                        backgroundColor: "#F90",
                        lineTension: 0,
                        fill: false,
                        data: this.chartBudgetData,
                        hidden: this.legendOffIndexes.indexOf(0) > -1
                    }
                );
            }

            if (this.timeline.some(t => t.forecastValues.length > 0)) {
                data.datasets.push(
                    {
                        label: "Forecast",
                        borderColor: "#0F9",
                        backgroundColor: "#0F9",
                        lineTension: 0,
                        fill: false,
                        data: this.chartForecastData,
                        hidden: this.legendOffIndexes.indexOf(1) > -1
                    }
                );
            }

            if (this.timeline.some(t => t.adjustedValues.length > 0)) {
                 data.datasets.push(
                     {
                        label: "Adjusted",
                        borderColor: "#72F",
                        backgroundColor: "#72F",
                        lineTension: 0,
                        fill: false,
                        data: this.chartAdjustedData,
                        hidden: this.legendOffIndexes.indexOf(2) > -1
                    },
                 );
            }

            if (this.timeline.some(t => t.actualValues.length > 0)) {
                data.datasets.push(
                    {
                        label: "Actual",
                        borderColor: "#09F",
                        backgroundColor: "#09F",
                        lineTension: 0,
                        fill: false,
                        data: this.chartActualData,
                        hidden: this.legendOffIndexes.indexOf(3) > -1
                    }
                );
            }

            return data;
        },
        chartTitle(){
            if (this.cropProfile.Variety && this.cropProfile.StartWeek && this.cropProfile.StartYear) {
                return `${this.cropProfile.Variety.VarietyName} (${this.cropProfile.Variety.VarietyDescription}, starting week ${this.cropProfile.StartWeek} ${this.cropProfile.StartYear})`;
            }

            return 'Production Curve';
        },
        chartOptions(){
            let _this = this;
            let options = {
                maintainAspectRatio: false,
                title: {
                    display: true,
                    text: this.chartTitle
                },
                legend: {
                    labels: {
                        usePointStyle: true
                    }
                },
                tooltips: {
                    callbacks: {
                        label: function(tooltipItem, data) {
                            var label = data.datasets[tooltipItem.datasetIndex].label || '';

                            if (label) {
                                label += ': ';
                            }
                            label += tooltipItem.yLabel.toFixed(2);
                            return label;
                        }
                    }
                },
                scales: {
                    yAxes: [{
                        scaleLabel: {
                            labelString: this.displayValueTypeCode.toUpperCase()
                        },
                        ticks: {
                            min: 0,
                            precision: 0
                        }
                    }],
                    xAxes: [{
                        type: "time",
                        time: {
                            unit: "week",
                            tooltipFormat: "YYYY-MM-DD"
                        },
                        ticks: {
                            source: "labels",
                            fontSize: 12,
                            maxRotation: 50,
                            minRotation: 50,
                            callback: function(value) {                                
                                return 'WK' + forecastingUtilService.getFinancialWeek(value, _this.financialYearCalendars) + ' FY' + forecastingUtilService.getFinancialYear(value, _this.financialYearCalendars);
                            }
                        }
                    }]
                }
            };

            return options;
        }
    },
    methods: {
        getDateForBudgetValue(budgetValue){
            return this.dates[budgetValue.BerriesForecastProfileWeekNumber - 1];
        },
        formatPercentage(percentage){
            if(this.displayValueTypeCode.toLowerCase() == "kg"){
                return percentage * (this.cropProfile.TonnesPerHectare * this.cropProfile.CropArea * 1000);
            }

            if(this.displayValueTypeCode.toLowerCase() == "%"){
                return 100 * percentage;
            }
        },
        formatKg(kg){
            if(this.displayValueTypeCode.toLowerCase() == "kg"){
                return kg;
            }

            if(this.displayValueTypeCode.toLowerCase() == "%"){
                let value = (kg / (this.cropProfile.TonnesPerHectare * this.cropProfile.CropArea * 1000)) * 100;
                return Math.round(value * 1000) / 1000;
            }
        },
        render(){
            if(this.$refs.cropProfileChart){
                this.$refs.cropProfileChart.render();
            }
        }
    },
    created() {
        this.legendOffIndexes = [];
        var _this = this;
        var original = Chart.defaults.global.legend.onClick;
        Chart.defaults.global.legend.onClick = function(e, legendItem) {
            if(_this.legendOffIndexes.indexOf(legendItem.datasetIndex) > -1) {
                _this.legendOffIndexes.splice(_this.legendOffIndexes.indexOf(legendItem.datasetIndex), 1);
            } else {
                _this.legendOffIndexes.push(legendItem.datasetIndex);
            }
            original.call(this, e, legendItem);
        };
    }
}
</script>